import React, { createContext, useState, useContext, ReactNode } from 'react';

type Language = 'nl' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  isTransitioning: boolean;
  t: (key: string) => string;
  updateTranslation: (key: string, lang: Language, value: string) => void;
}

const initialTranslations: Record<Language, Record<string, string>> = {
  nl: {
    'nav.home': 'Home',
    'nav.overview': 'Ons aanbod',
    'nav.contact': 'Contact',
    'nav.rent': 'Verhuren',
    'nav.admin': 'Inloggen',
    'nav.exit': 'Uitloggen',
    
    // Landing
    'landing.hero.title': 'Ervaar pure rust in de natuur',
    'landing.hero.subtitle': 'Persoonlijk geselecteerd en met zorg gekeurd.',
    'landing.cta': 'Bekijk ons aanbod',
    'landing.about.title': 'Chalet Inn',
    'landing.about.text': 'Chalet Inn heeft de mooiste chalets in de natuur en voor mensen die opzoek zijn naar rust. Wij verhuren door heel Nederland chalets, van de waddeneilanden tot aan Limburg wij zijn overal te vinden.',
    'landing.features.1': 'Unieke Locaties',
    'landing.features.2': 'Premium Service',
    'landing.features.3': 'Volledige Privacy',
	'guestbook.title': 'Wat gasten zeggen',
	'guestbook.desc': 'Authentieke ervaringen van reizigers die u voorgingen',
	'map.title': 'Ontdek onze locaties',
	'map.desc1': 'Chalet Inn heeft de mooiste chalets in de natuur en voor mensen die op zoek zijn naar rust. Wij verhuren door heel Nederland chalets, van de waddeneilanden tot aan Limburg; wij zijn overal te vinden.',
	'map.desc2': 'Bekijk in één oogopslag waar onze chalets liggen en klik op een marker om direct een verblijf te bekijken.',

    // Listings (Overview)
    'hero.searchPlaceholder': 'Waar wil je heen?',
    'filter.all': 'Alle',
    'filter.recommended': 'Aanbevolen',
    'filter.priceAsc': 'Prijs: Laag → Hoog',
    'filter.priceDesc': 'Prijs: Hoog → Laag',
    'filter.reset': 'Reset alle filters',
    'filter.category': 'Categorie',
    'filter.facilities': 'Voorzieningen',
    'filter.price': 'Prijs per nacht',
    'filter.priceMax': 'Max. prijs',
    'filter.guests': 'Aantal gasten',
    'home.found': 'verblijven gevonden',
    'home.notFound': 'Geen huisjes gevonden',
    'home.notFoundDesc': 'Probeer je zoekopdracht of filters aan te passen.',
    
    // Cards & Detail
    'card.from': 'Vanaf',
    'card.night': 'nacht',
    'card.details': 'Bekijk details',
    'detail.back': 'Terug naar overzicht',
    'detail.guests': 'gasten',
    'detail.reviews': 'Reviews',
    'detail.about': 'Over dit verblijf',
    'detail.amenities': 'Voorzieningen',
    'detail.checkAvailability': 'Check beschikbaarheid',
    'detail.bookNow': 'Reserveer nu',
    'detail.chooseDates': 'Kies data',
    'detail.popular': '🔥 Populair! Deze maand veel geboekt.',
    'detail.total': 'Totaal',
    'detail.cleaning': 'Schoonmaakkosten',
	'detail.highprice': 'Afwijkende prijs per nacht',
    
    // Checkout
    'checkout.title': 'Je boeking afronden',
    'checkout.step1': 'Extra opties',
    'checkout.step2': 'Gegevens & Betaling',
    'checkout.continue': 'Doorgaan naar betalen',
    'checkout.pay': 'Betaal',
    'checkout.name': 'Volledige naam',
    'checkout.secure': 'Veilige betaling via Stripe',
    'checkout.nights': 'Nachten',
    'checkout.stay': 'Verblijf',
    
    // Categories
    'cat.Natuurhuisje': 'Natuurhuisje',
    'cat.Appartement': 'Appartement',
    'cat.Villa': 'Villa',
    'cat.Kasteel': 'Kasteel',

    // Amenities (Default)
    'amenity.wifi': 'Gratis Wifi',
    'amenity.sauna': 'Sauna',
    'amenity.pets': 'Huisdieren toegestaan',
    'amenity.hiking': 'Wandelroutes',
    'amenity.kitchen': 'Keuken',
    'amenity.parking': 'Gratis Parkeren',
    'amenity.BBQ': 'BBQ',
    'amenity.TV': 'TV',
    'amenity.jacuzzi': 'Jacuzzi',
    'amenity.garden': 'Grote tuin',
    'amenity.kidsteam': 'Animatieteam',
    'amenity.cycle': 'Fietsroutes',
    'amenity.gym': 'Gym',
    'amenity.fireplace': 'Houtkachel',
    'amenity.roof': 'Overkapping',
    'amenity.woods': 'Bosrijke omgeving',
	'amenity.cycle2': 'Fietsen',
	'amenity.bed': 'Bedstee',
	'amenity.terrace': 'Terras',
	'amenity.beach': 'Strand',
	'amenity.airconditioning': 'Airco',
	'amenity.boat': 'Bootverhuur',
	'amenity.vent': 'Ventilator',
	'amenity.restaurant': 'Restaurant',
	'amenity.heating': 'Verwarming',
	'amenity.watersport': 'Watersportactiviteiten',
	'amenity.pool': 'Zwembad',
	'amenity.wash': 'Wasserette',
	'amenity.tennis': 'Tennisbaan',
	'amenity.wintersport': 'Wintersport',
	'amenity.mountain': 'Bergen',
	
	
	// Rent
    'rent.title': 'Uw eigendom verhuren via Chalet Inn',
    'rent.subtitle': 'Sluit u aan bij onze prachtige collectie en bereik gasten die zoeken naar de ultieme ervaring.',
	'rent.benefits.title': 'Waarom Chalet Inn?',
	'rent.benefits.1.title': 'Volledige ontzorging',
	'rent.benefits.1.text': 'Van boeking tot betaling; wij regelen het volledige administratieve proces.',
	'rent.benefits.2.title': 'Gegarandeerde kwaliteit door persoonlijke selectie',
	'rent.benefits.2.text': 'Alle vakantiewoningen zijn door ons persoonlijk geselecteerd en goedgekeurd op kwaliteit. Dat zorgt voor een consistent hoog niveau.',
	'rent.benefits.3.title': 'Persoonlijke service die gasten laat terugkomen',
	'rent.benefits.3.text': 'Chalet Inn is geen anoniem massaplatform, maar een persoonlijke en betrouwbare partner. Dankzij de hoge kwaliteit, aandacht voor detail en betrokken service is het niet voor niets dat gasten graag bij ons terugkomen.',
	'rent.form.title': 'Word ook een host',

    // Contact
    'contact.title': 'Neem contact op',
    'contact.subtitle': 'Heeft u vragen over een boeking of over iets anders?',
    'contact.details': 'Gegevens',
    'contact.phone': 'Telefoon',
    'contact.phonenumber': '085-7602420',
    'contact.email': 'Email',
    'contact.emailaddress': 'info@chaletinn.nl',
    'contact.address': 'Hoofdkantoor',
    'contact.open': 'Ma-Zo: 09:00 - 21:00',
    'contact.response': 'Binnen 2 uur antwoord',
    'contact.form.title': 'Stuur een bericht',
    'contact.form.phone': 'Telefoonnummer',
    'contact.form.firstname': 'Voornaam',
    'contact.form.lastname': 'Achternaam',
    'contact.form.email': 'Emailadres',
    'contact.form.subject': 'Onderwerp',
    'contact.form.message': 'Bericht',
    'contact.form.send': 'Verstuur bericht',
    'contact.sub.general': 'Algemene vraag',
    'contact.sub.booking': 'Vraag over een boeking',
    'contact.sub.hosting': 'Ik wil verhuren',

    // Admin
    'admin.tabs.chalets': 'Mijn Chalets',
    'admin.tabs.bookings': 'Boekingen',
    'admin.tabs.translations': 'Translations',
    'admin.tabs.extras': 'Extra Opties',
    'admin.tabs.stripe': 'Stripe',

    'admin.settings.stripe': 'Betalingsgateway (Stripe)',
    'admin.settings.stripe.desc': 'Configureer hier je API sleutels om betalingen te ontvangen.',
    'admin.settings.stripe.pub': 'Public Key (PK)',
    'admin.settings.stripe.sec': 'Secret Key (SK)',
    'admin.settings.save': 'Opslaan',
    'admin.settings.saved': 'Instellingen opgeslagen!',
    
    'admin.extras.title': 'Beheer Extra Opties',
    'admin.extras.desc': 'Voeg opties toe die gasten tijdens het boeken kunnen selecteren, zoals ontbijt of fietsverhuur.',
    'admin.extras.edit': 'Bewerken',
    'admin.extras.cancel': 'Annuleren',
    'admin.extras.update': 'Update',
    'admin.extras.add': 'Toevoegen',

    // Admin Bookings
    'admin.bookings.filter.all': 'Alle',
    'admin.bookings.filter.confirmed': 'Bevestigd',
    'admin.bookings.filter.pending': 'In afwachting',
    'admin.bookings.filter.cancelled': 'Geannuleerd',
    'admin.bookings.status': 'Status',
    'admin.bookings.status.confirmed': 'Bevestigd',
    'admin.bookings.status.pending': 'In afwachting',
    'admin.bookings.status.cancelled': 'Geannuleerd',
  },
  en: {
    'nav.home': 'Home',
    'nav.overview': 'Listings',
    'nav.contact': 'Contact',
    'nav.admin': 'Login (Hosts)',
    'nav.exit': 'Exit Admin',

    // Landing
    'landing.hero.title': 'Experience pure luxury in nature',
    'landing.hero.subtitle': 'The most beautiful chalets and apartments in unique locations.',
    'landing.cta': 'View our listings',
    'landing.about.title': 'About Chalet Inn',
    'landing.about.text': 'Chalet Inn was born from a love for unique places. We select only the most exclusive, comfortable, and luxurious stays in nature and vibrant cities. Whether you are looking for a quiet spot in the Veluwe or a penthouse in Maastricht, we ensure an unforgettable experience.',
    'landing.features.1': 'Unique Locations',
    'landing.features.2': 'Premium Service',
    'landing.features.3': 'Complete Privacy',

    // Listings
    'hero.searchPlaceholder': 'Where do you want to go? (e.g. Veluwe)',
    'filter.all': 'All',
    'filter.recommended': 'Recommended',
    'filter.priceAsc': 'Price: Low → High',
    'filter.priceDesc': 'Price: High → Low',
    'filter.reset': 'Reset all filters',
    'filter.category': 'Category',
    'filter.facilities': 'Facilities',
    'filter.price': 'Price per night',
    'filter.priceMax': 'Max. price',
    'filter.guests': 'Guests',
    'home.found': 'stays found',
    'home.notFound': 'No properties found',
    'home.notFoundDesc': 'Try adjusting your search or filters.',
    
    // Cards & Detail
    'card.from': 'From',
    'card.night': 'night',
    'card.details': 'View details',
    'detail.back': 'Back to overview',
    'detail.guests': 'guests',
    'detail.reviews': 'reviews',
    'detail.about': 'About this stay',
    'detail.amenities': 'Amenities',
    'detail.checkAvailability': 'Check availability',
    'detail.bookNow': 'Book now',
    'detail.chooseDates': 'Select dates',
    'detail.popular': '🔥 Popular! Booked often this month.',
    'detail.total': 'Total',
    'detail.cleaning': 'Cleaning fee',
	'detail.highprice': 'Higher price per night',
    
    // Checkout
    'checkout.title': 'Complete your booking',
    'checkout.step1': 'Extra options',
    'checkout.step2': 'Details & Payment',
    'checkout.continue': 'Continue to payment',
    'checkout.pay': 'Pay',
    'checkout.name': 'Full name',
    'checkout.secure': 'Secure payment via Stripe',
    'checkout.nights': 'Nights',
    'checkout.stay': 'Stay',
    
    // Categories
    'cat.Natuurhuisje': 'Nature Cottage',
    'cat.Appartement': 'Apartment',
    'cat.Villa': 'Villa',
    'cat.Kasteel': 'Castle',

    // Amenities (Default)
    'amenity.wifi': 'Free Wifi',
    'amenity.sauna': 'Private Sauna',
    'amenity.pets': 'Pets allowed',
    'amenity.hiking': 'Hiking trails',
    'amenity.kitchen': 'Luxury Kitchen',
    'amenity.parking': 'Free Parking',

    // Contact
    'contact.title': 'Contact us',
    'contact.subtitle': 'Do you have questions about a booking or want to rent out your own chalet? Our team is available 7 days a week.',
    'contact.details': 'Details',
    'contact.phone': 'Phone',
    'contact.phonenumber': '+31 6 23536682',
    'contact.email': 'Email',
    'contact.emailaddress': 'info@chaletinn.nl',
    'contact.address': 'Headquarters',
    'contact.open': 'Mon-Sun: 09:00 - 21:00',
    'contact.response': 'Response within 2 hours',
    'contact.form.title': 'Send a message',
    'contact.form.firstname': 'First name',
    'contact.form.lastname': 'Last name',
    'contact.form.email': 'Email address',
    'contact.form.subject': 'Subject',
    'contact.form.message': 'Message',
    'contact.form.send': 'Send message',
    'contact.sub.general': 'General question',
    'contact.sub.booking': 'Question about booking',
    'contact.sub.hosting': 'I want to host',

    // Admin
    'admin.tabs.chalets': 'My Chalets',
    'admin.tabs.bookings': 'Bookings',
    'admin.tabs.translations': 'Translations',
    'admin.tabs.extras': 'Extra Options',
    'admin.tabs.stripe': 'Stripe',

    'admin.settings.stripe': 'Payment Gateway (Stripe)',
    'admin.settings.stripe.desc': 'Configure your API keys here to receive payments.',
    'admin.settings.stripe.pub': 'Public Key (PK)',
    'admin.settings.stripe.sec': 'Secret Key (SK)',
    'admin.settings.save': 'Save',
    'admin.settings.saved': 'Settings saved!',

    'admin.extras.title': 'Manage Extra Options',
    'admin.extras.desc': 'Add options that guests can select during booking, like breakfast or bike rentals.',
    'admin.extras.edit': 'Edit',
    'admin.extras.cancel': 'Cancel',
    'admin.extras.update': 'Update',
    'admin.extras.add': 'Add',
    
    // Admin Bookings
    'admin.bookings.filter.all': 'All',
    'admin.bookings.filter.confirmed': 'Confirmed',
    'admin.bookings.filter.pending': 'Pending',
    'admin.bookings.filter.cancelled': 'Cancelled',
    'admin.bookings.status': 'Status',
    'admin.bookings.status.confirmed': 'Confirmed',
    'admin.bookings.status.pending': 'Pending',
    'admin.bookings.status.cancelled': 'Cancelled',
  }
};

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>('nl');
  const [translations, setTranslations] = useState(initialTranslations);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const t = (key: string): string => {
    return translations[language][key] || key;
  };

  const setLanguage = (lang: Language) => {
    if (lang === language) return;
    setIsTransitioning(true);
    // Wait for fade out
    setTimeout(() => {
        setLanguageState(lang);
        setIsTransitioning(false); // Trigger fade in
    }, 300);
  };

  const updateTranslation = (key: string, lang: Language, value: string) => {
    setTranslations(prev => ({
        ...prev,
        [lang]: {
            ...prev[lang],
            [key]: value
        }
    }));
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, isTransitioning, t, updateTranslation }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};